"use client";

import {
  Button,
  createSortableColumn,
  DataTable,
  Input,
  Skeleton,
  type TanstackReactTable,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ClockIcon,
  LandmarkIcon,
  ShieldCheckIcon,
  StoreIcon,
  TrendingUpIcon,
  UserIcon,
  WrenchIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { formatTimeAgo } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { WalletAccountItem } from "../api/client";
import {
  useWalletAccountsQuery,
  useWalletAccountsStatsQuery,
} from "../api/queries";
import { WalletAccountKycTierCell } from "../ui/WalletAccountKycTierCell";

const OWNER_TYPE_OPTIONS = [
  { value: "all", label: "ປະເພດທັງໝົດ" },
  { value: "customer", label: "ລູກຄ້າ" },
  { value: "shop", label: "ຮ້ານຄ້າ" },
  { value: "mechanic", label: "ຊ່າງ" },
  { value: "platform", label: "ແພລດຟອມ" },
] as const;

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "frozen", label: "ຖືກລ໋ອກ" },
  { value: "closed", label: "ປິດແລ້ວ" },
] as const;

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  active: {
    dot: "bg-success",
    text: "text-success",
    label: "ໃຊ້ງານ",
  },
  frozen: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ຖືກລ໋ອກ",
  },
  closed: {
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    label: "ປິດແລ້ວ",
  },
};

function OwnerIcon({ type }: { type: string }) {
  switch (type) {
    case "customer":
      return <UserIcon className="size-5 text-secondary" />;
    case "shop":
      return <StoreIcon className="size-5 text-secondary" />;
    case "mechanic":
      return <WrenchIcon className="size-5 text-secondary" />;
    case "platform":
      return <LandmarkIcon className="size-5 text-primary" />;
    default:
      return <UserIcon className="size-5 text-muted-foreground" />;
  }
}

function AccountsStatsCards() {
  const { data: stats, isLoading } = useWalletAccountsStatsQuery();

  if (isLoading) {
    return (
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <SummaryCard
        title="ປະລິມານທຸລະກຳ (24 ຊມ)"
        icon={TrendingUpIcon}
        color="orange"
        items={[{ label: "", value: formatPrice(stats.volume24h, "LAK") }]}
        subtitle="24 ຊົ່ວໂມງທີ່ຜ່ານມາ"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ອັດຕາການປະຕິບັດຕາມ"
        icon={ShieldCheckIcon}
        color="success"
        items={[{ label: "", value: `${stats.complianceRate}%` }]}
        subtitle="ບັນຊີທີ່ active / ທັງໝົດ"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ລໍຖ້າດຳເນີນການ"
        icon={ClockIcon}
        color={stats.pendingActions > 0 ? "destructive" : "muted"}
        items={[{ label: "", value: stats.pendingActions.toLocaleString() }]}
        subtitle="ທຸລະກຳທີ່ລໍຖ້າ"
        subtitleClassName="text-muted-foreground"
      />
    </div>
  );
}

export function WalletAccountsPage() {
  const nav = useNavigate({ from: "/app/wallet/accounts" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/accounts",
  });

  const list = useWalletAccountsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const ownerTypeFilter = findCondition(filters, "ownerType");
  const statusFilter = findCondition(filters, "status");
  const ownerIdFilter = findCondition(filters, "ownerId");

  const [searchValue, setSearchValue] = useState<string>(
    (ownerIdFilter?.value as string) || "",
  );

  useEffect(() => {
    setSearchValue((ownerIdFilter?.value as string) || "");
  }, [ownerIdFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "ownerId", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "ownerId");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const ownerTypeValue = (ownerTypeFilter?.value as string) || "all";
  const statusValue = (statusFilter?.value as string) || "all";
  const hasFilter =
    !!ownerIdFilter?.value || ownerTypeValue !== "all" || statusValue !== "all";

  const columns: TanstackReactTable.ColumnDef<WalletAccountItem>[] = [
    {
      id: "owner",
      header: "ເຈົ້າຂອງ",
      cell: ({ row }) => {
        const w = row.original;
        const hasImage = !!w.ownerImage;
        return (
          <div className="flex items-center gap-3">
            {hasImage ? (
              <img
                src={resolveImageSrc(w.ownerImage) ?? ""}
                alt={w.ownerName ?? ""}
                className="size-10 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
                <OwnerIcon type={w.ownerType} />
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate font-medium text-sm">
                {w.ownerName ?? "—"}
              </div>
              <div className="text-muted-foreground text-xs">
                {w.ownerId ? w.ownerId.slice(0, 12) : "—"}
              </div>
            </div>
          </div>
        );
      },
      size: 260,
    },
    {
      id: "ownerType",
      header: "ປະເພດ",
      cell: ({ row }) => {
        const t = row.original.ownerType;
        return (
          <div className="flex items-center gap-2">
            <OwnerIcon type={t} />
            <span className="text-sm">{OWNER_TYPE_LABELS[t] ?? t}</span>
          </div>
        );
      },
      size: 130,
    },
    {
      id: "balance",
      header: "ຍອດຄົງເຫຼືອ",
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">
          {formatPrice(row.original.balance, row.original.currency)}
        </span>
      ),
      size: 180,
    },
    // {
    //   id: "currency",
    //   header: "ສະກຸນເງິນ",
    //   cell: ({ row }) => (
    //     <span className="text-muted-foreground text-sm">
    //       {row.original.currency}
    //     </span>
    //   ),
    //   size: 100,
    // },
    {
      id: "kycTier",
      header: "KYC Tier",
      cell: ({ row }) => (
        <WalletAccountKycTierCell
          walletId={row.original.id}
          kycTier={row.original.kycTier}
        />
      ),
      size: 160,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 120,
    },
    createSortableColumn<WalletAccountItem>("updatedAt", "ອັບເດດລ່າສຸດ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.updatedAt ? formatTimeAgo(row.original.updatedAt) : "—"}
        </span>
      ),
    }),
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ບັນຊີກະເປົາເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between space-y-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">ບັນຊີກະເປົາເງິນ</h2>
              <p className="text-muted-foreground">
                ຈັດການກະເປົາເງິນຂອງລູກຄ້າ, ຮ້ານຄ້າ ແລະ ຊ່າງ
              </p>
            </div>
          </div>
        </div>

        <AccountsStatsCards />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          {/* Filter bar */}
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <Input
                  placeholder="ຄົ້ນຫາ Owner ID..."
                  defaultValue={searchValue}
                  onChange={(e) => debounced(e.target.value)}
                  className="h-8 sm:max-w-2xs"
                />
                <SimpleSelect
                  value={ownerTypeValue}
                  onValueChange={(val) => {
                    let next: FilterConditionDTO[] | undefined = filters;
                    if (val === "all") {
                      next = removeConditions(filters, "ownerType");
                    } else {
                      next = upsertCondition(filters, {
                        field: "ownerType",
                        op: "eq",
                        value: val,
                      });
                    }
                    nav({
                      search: {
                        ...search,
                        offset: 0,
                        filters: next?.length ? next : undefined,
                      },
                    });
                  }}
                  placeholder="ປະເພດ"
                  className="h-8 w-full sm:w-40"
                  options={OWNER_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={statusValue}
                  onValueChange={(val) => {
                    let next: FilterConditionDTO[] | undefined = filters;
                    if (val === "all") {
                      next = removeConditions(filters, "status");
                    } else {
                      next = upsertCondition(filters, {
                        field: "status",
                        op: "eq",
                        value: val,
                      });
                    }
                    nav({
                      search: {
                        ...search,
                        offset: 0,
                        filters: next?.length ? next : undefined,
                      },
                    });
                  }}
                  placeholder="ສະຖານະ"
                  className="h-8 w-full sm:w-40"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: { ...search, filters: undefined, offset: 0 },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="h-4 w-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<WalletAccountItem, unknown>
            noDataMessage="ບໍ່ພົບບັນຊີກະເປົາເງິນ"
            isLoading={list.isLoading}
            columns={columns}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
